import React, { useState } from 'react';
import axios from 'axios';
import API_URL from './config';

function CallManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [updateData, setUpdateData] = useState({
    job_status: '',
    close_date: '',
    remark: '',
    cancellation_reason: ''
  });

  // Reassignment state
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignJob, setReassignJob] = useState(null);
  const [availableASCs, setAvailableASCs] = useState([]);
  const [selectedASC, setSelectedASC] = useState('');
  const [reassignReason, setReassignReason] = useState('');

  // Search jobs
  const searchJobs = async () => {
    if (!searchTerm.trim()) {
      showMessage('error', 'Please enter a Job Number');
      return;
    }

    try {
      setSearchLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/jobs/search?q=${searchTerm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSearchResults(response.data);
      if (response.data.length === 0) {
        showMessage('info', 'No jobs found with this number');
      }
    } catch (error) {
      showMessage('error', 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  // Select a job to update
  const selectJob = (job) => {
    setSelectedJob(job);
    setUpdateData({
      job_status: job.job_status || '',
      close_date: job.close_date ? job.close_date.split('T')[0] : '',
      remark: job.remark || '',
      cancellation_reason: ''
    });
  };

  // Open reassign modal and fetch ASC list
  const openReassignModal = async (job) => {
    setReassignJob(job);
    setSelectedASC('');
    setReassignReason('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/jobs/asc-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableASCs(response.data);
      setReassignModalOpen(true);
    } catch (error) {
      showMessage('error', 'Failed to load ASC list');
    }
  };

  // Handle reassignment submission
  const handleReassign = async () => {
    if (!selectedASC) {
      showMessage('error', 'Please select an ASC');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(`${API_URL}/api/jobs/reassign`, {
        job_no: reassignJob.job_no,
        new_asc_id: selectedASC,
        reason: reassignReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showMessage('success', `Job reassigned successfully`);
        setReassignModalOpen(false);
        searchJobs();
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Reassignment failed');
    } finally {
      setLoading(false);
    }
  };

  // Update job status
  const updateJobStatus = async () => {
    if (!updateData.job_status) {
      showMessage('error', 'Please select a status');
      return;
    }

    if (updateData.job_status === 'Closed' && !updateData.close_date) {
      showMessage('error', 'Please select close date for closed jobs');
      return;
    }

    if (updateData.job_status === 'Cancelled' && !updateData.cancellation_reason) {
      showMessage('error', 'Please provide cancellation reason');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const payload = {
        job_no: selectedJob.job_no,
        job_status: updateData.job_status,
        close_date: updateData.close_date || null,
        remark: updateData.remark,
        cancellation_reason: updateData.cancellation_reason,
        updated_by: JSON.parse(localStorage.getItem('user')).name
      };

      const response = await axios.post(`${API_URL}/api/jobs/update-status`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        showMessage('success', `Job ${selectedJob.job_no} updated to ${updateData.job_status}`);

        searchJobs();

        setSelectedJob(null);
        setUpdateData({
          job_status: '',
          close_date: '',
          remark: '',
          cancellation_reason: ''
        });
      }
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeStyle = (status) => {
    switch(status) {
      case 'Closed':
        return { bg: 'var(--system-green)', color: 'white' };
      case 'Cancelled':
        return { bg: 'var(--system-red)', color: 'white' };
      case 'In Progress':
        return { bg: 'var(--system-blue)', color: 'white' };
      case 'Pending':
        return { bg: 'var(--system-orange)', color: 'white' };
      default:
        return { bg: 'var(--system-fill)', color: 'var(--system-label)' };
    }
  };

  return (
    <div className="fade-in" style={styles.container}>
      <h1 style={styles.header}>Call Management</h1>

      {/* Message Display */}
      {message.text && (
        <div className={`message-toast ${message.type}`} style={{ position: 'static', marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {/* Search Section */}
      <div className="premium-card" style={styles.searchCard}>
        <h3 style={styles.sectionTitle}>Search Job</h3>
        <div style={styles.searchBox}>
          <input
            type="text"
            placeholder="Enter Job Number (e.g., RKT202602092958)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="premium-input"
            style={styles.searchInput}
            onKeyPress={(e) => e.key === 'Enter' && searchJobs()}
          />
          <button
            onClick={searchJobs}
            disabled={searchLoading}
            className="premium-button"
            style={styles.searchButton}
          >
            {searchLoading ? (
              <span className="loading-spinner" style={{ width: 20, height: 20, margin: '0 auto' }} />
            ) : (
              'Search'
            )}
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && !selectedJob && (
        <div className="premium-table-container" style={styles.resultsCard}>
          <h3 style={styles.sectionTitle}>Search Results</h3>
          <div style={styles.tableContainer}>
            <table className="premium-table" style={styles.table}>
              <thead>
                <tr>
                  <th>Job No.</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>ASC</th>
                  <th>Allocation Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((job) => {
                  const statusStyle = getStatusBadgeStyle(job.job_status);
                  return (
                    <tr key={job.id} style={styles.tableRow}>
                      <td><strong style={styles.jobNo}>{job.job_no}</strong></td>
                      <td>{job.customer_name}</td>
                      <td>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color
                        }}>
                          {job.job_status || 'Pending'}
                        </span>
                      </td>
                      <td>{job.allocated_asc_name || 'Not Allocated'}</td>
                      <td>{formatDate(job.allocation_date)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => selectJob(job)}
                            className="premium-button success"
                            style={styles.selectButton}
                          >
                            Update Status
                          </button>
                          <button
                            onClick={() => openReassignModal(job)}
                            className="premium-button info"
                            style={styles.selectButton}
                          >
                            Reassign
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Update Form */}
      {selectedJob && (
        <div className="premium-card" style={styles.updateCard}>
          <h3 style={styles.sectionTitle}>Update Job: {selectedJob.job_no}</h3>

          <div style={styles.jobDetails}>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Customer</span>
                <span style={styles.detailValue}>{selectedJob.customer_name}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Contact</span>
                <span style={styles.detailValue}>{selectedJob.contact_no}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Product</span>
                <span style={styles.detailValue}>{selectedJob.product} - {selectedJob.model}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>Current Status</span>
                <span style={{
                  ...styles.statusBadge,
                  ...getStatusBadgeStyle(selectedJob.job_status),
                  marginLeft: '0'
                }}>
                  {selectedJob.job_status || 'Pending'}
                </span>
              </div>
            </div>
          </div>

          <div style={styles.formContainer}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Update Status</label>
              <select
                value={updateData.job_status}
                onChange={(e) => setUpdateData({...updateData, job_status: e.target.value})}
                className="premium-select"
                style={styles.select}
              >
                <option value="">Select Status</option>
                <option value="Closed">Closed - Completed</option>
                <option value="Cancelled">Cancelled</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
                <option value="Reallocated">Reallocated</option>
              </select>
            </div>

            {updateData.job_status === 'Closed' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Close Date</label>
                <input
                  type="date"
                  value={updateData.close_date}
                  onChange={(e) => setUpdateData({...updateData, close_date: e.target.value})}
                  className="premium-input"
                  style={styles.input}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            )}

            {updateData.job_status === 'Cancelled' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Cancellation Reason</label>
                <select
                  value={updateData.cancellation_reason}
                  onChange={(e) => setUpdateData({...updateData, cancellation_reason: e.target.value})}
                  className="premium-select"
                  style={styles.select}
                >
                  <option value="">Select Reason</option>
                  <option value="Customer not reachable">Customer not reachable</option>
                  <option value="Customer cancelled">Customer cancelled</option>
                  <option value="Duplicate job">Duplicate job</option>
                  <option value="Wrong information">Wrong information</option>
                  <option value="Out of warranty">Out of warranty</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Remarks (Optional)</label>
              <textarea
                value={updateData.remark}
                onChange={(e) => setUpdateData({...updateData, remark: e.target.value})}
                className="premium-input"
                style={styles.textarea}
                placeholder="Add any additional remarks..."
                rows="3"
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                onClick={updateJobStatus}
                disabled={loading}
                className="premium-button success"
                style={styles.updateButton}
              >
                {loading ? (
                  <span className="loading-spinner" style={{ width: 20, height: 20 }} />
                ) : (
                  'Update Job Status'
                )}
              </button>
              <button
                onClick={() => {
                  setSelectedJob(null);
                  setUpdateData({
                    job_status: '',
                    close_date: '',
                    remark: '',
                    cancellation_reason: ''
                  });
                }}
                className="premium-button secondary"
                style={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Modal */}
      {reassignModalOpen && reassignJob && (
        <div style={styles.modalOverlay}>
          <div className="premium-card" style={styles.modal}>
            <h3 style={styles.modalTitle}>Reassign Job: {reassignJob.job_no}</h3>

            <div style={styles.modalContent}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Select ASC</label>
                <select
                  value={selectedASC}
                  onChange={(e) => setSelectedASC(e.target.value)}
                  className="premium-select"
                  style={styles.select}
                >
                  <option value="">-- Select ASC --</option>
                  {availableASCs.map(asc => (
                    <option key={asc.id} value={asc.id}>
                      {asc.asp_name} - {asc.city} ({asc.coverage_pincode})
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Reason for Reassignment</label>
                <textarea
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  className="premium-input"
                  style={styles.textarea}
                  placeholder="e.g., ASC unable to attend, customer request, etc."
                  rows="2"
                />
              </div>
            </div>

            <div style={styles.modalActions}>
              <button onClick={handleReassign} className="premium-button success" disabled={loading}>
                {loading ? 'Reassigning...' : 'Confirm Reassignment'}
              </button>
              <button onClick={() => setReassignModalOpen(false)} className="premium-button secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="premium-card" style={styles.statsCard}>
        <h4 style={styles.statsTitle}>Quick Stats</h4>
        <div style={styles.statsGrid}>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Total Jobs Found</span>
            <span style={styles.statValue}>{searchResults.length}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Selected Job</span>
            <span style={styles.statValue}>{selectedJob ? selectedJob.job_no : 'None'}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Search Term</span>
            <span style={styles.statValue}>{searchTerm || '—'}</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Status</span>
            <span style={styles.statValue}>
              {selectedJob ? (selectedJob.job_status || 'Pending') : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Keyboard shortcut hint */}
      <div style={styles.hint}>
        <span style={styles.hintIcon}>⌘</span>
        <span style={styles.hintText}>Press Enter to search</span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
    animation: 'fadeIn 0.3s ease'
  },
  header: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--system-label)',
    marginBottom: '32px',
    textAlign: 'center',
    letterSpacing: '-0.022em'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--system-label)',
    marginBottom: '16px'
  },
  searchCard: {
    marginBottom: '24px',
    padding: '24px'
  },
  searchBox: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  searchInput: {
    flex: 1,
    height: '48px'
  },
  searchButton: {
    minWidth: '100px',
    height: '48px'
  },
  resultsCard: {
    marginBottom: '24px',
    animation: 'slideIn 0.3s ease'
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '12px'
  },
  table: {
    minWidth: '800px'
  },
  tableRow: {
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  },
  jobNo: {
    color: 'var(--system-blue)',
    fontFamily: 'monospace'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    letterSpacing: '-0.01em'
  },
  selectButton: {
    padding: '6px 12px',
    fontSize: '12px',
    minWidth: '70px'
  },
  updateCard: {
    marginBottom: '24px',
    padding: '28px',
    animation: 'slideIn 0.3s ease'
  },
  jobDetails: {
    background: 'var(--system-fill)',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '24px'
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  detailLabel: {
    fontSize: '12px',
    color: 'var(--system-secondary-label)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  detailValue: {
    fontSize: '15px',
    fontWeight: '500',
    color: 'var(--system-label)'
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--system-secondary-label)'
  },
  select: {
    height: '48px'
  },
  input: {
    height: '48px'
  },
  textarea: {
    resize: 'vertical',
    minHeight: '100px'
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px'
  },
  updateButton: {
    flex: 2,
    height: '48px'
  },
  cancelButton: {
    flex: 1,
    height: '48px'
  },
  statsCard: {
    padding: '20px',
    marginTop: '24px'
  },
  statsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--system-label)',
    marginBottom: '16px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px'
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px',
    background: 'var(--system-fill)',
    borderRadius: '12px'
  },
  statLabel: {
    fontSize: '12px',
    color: 'var(--system-secondary-label)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  statValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--system-blue)'
  },
  hint: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    background: 'var(--glass-background)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid var(--glass-border)',
    borderRadius: '30px',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: 'var(--system-shadow-md)',
    zIndex: 100
  },
  hintIcon: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--system-blue)'
  },
  hintText: {
    fontSize: '13px',
    color: 'var(--system-secondary-label)'
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(5px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    width: '90%',
    maxWidth: '500px',
    padding: '30px'
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  modalContent: {
    marginBottom: '24px'
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
  }
};

// Add animation keyframes
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(style);

export default CallManagement;